import React, { useEffect } from 'react';
import getReceivedAt from '../../utilities/getReceivedAt.js';
import { fromNow } from 'utilities/fromNowWrapper.js';
import { GeckoCardTable, GeckoLabel } from '../../ui';
import Loader from 'components/Loader';
import './index.scss';
import { GeckoModalService, GeckoBtn, GECKO_CONSTS } from '../../ui';
import MessageListModal from './components/MessageListModal.jsx';
import HighlightedText from './components/HighlightedText.jsx';
import { useRedux } from 'services/gecko-store/redux';
import ConversationErrorContainer from 'routes/conversations/components/ConversationErrorContainer';
import CONSTS from 'constants';
import GeckoLocation from 'services/gecko-location';
import frontTruncate from './helpers/frontTruncate.js';
import ResultsHeader from './components/ResultsHeader.jsx';
import CloseConversationModal from './components/CloseConversationModal.jsx';
import AddTagsModal from './components/AddTagsModal.jsx';
import AssignConversationModal from './components/AssignConversationModal.jsx';
import AddMassTagsModal from './components/AddMassTagsModal.jsx';
import MassCloseConversationModal from './components/MassCloseConversationModal.jsx';
import MassAssignConversationModal from './components/MassAssignConversationModal.jsx';
import useResultsData from './hooks/useResultsData.jsx';
import SearchFilterBar from './components/SearchFilterBar.jsx';
import NavBar from '../../components/NavBar.jsx';
import GeckoPermissions from 'services/gecko-permissions';
import buildFilterQueries from './helpers/buildFilterQueries.js';
import GeckoStore from 'services/gecko-store';

const mapStateToProps = (state) => {
  return {
    searchTerm: state.location.params.search || '',
    paramAgents: state.location.params.agent,
    paramTeams: state.location.params.team,
    paramChannels: state.location.params.channel,
    paramTags: state.location.params.tag,
    paramStatuses: state.location.params.status || [],
    webAvailable: state.appState.loggedInUser.web_available,
    mobileAvailable: state.appState.loggedInUser.mobile_available,
  };
};

function Conversations() {
  const {
    searchTerm,
    paramAgents,
    paramTeams,
    paramChannels,
    paramTags,
    paramStatuses,
    webAvailable,
    mobileAvailable,
  } = useRedux(mapStateToProps);

  const ensureArray = (param) => (Array.isArray(param) ? param : [param]);

  const isNonNegativeInt = (value) => {
    const num = parseInt(value, 10);
    return !isNaN(num) && num >= 0;
  };

  const agentsFromParams = ensureArray(paramAgents)
    .filter(isNonNegativeInt)
    .map((agentId) => `agent_${agentId}`);

  const teamsFromParams = ensureArray(paramTeams)
    .filter(isNonNegativeInt)
    .map((teamId) => `team_${teamId}`);

  const channelsFromParams =
    ensureArray(paramChannels).filter(isNonNegativeInt);

  const tagsFromParams = ensureArray(paramTags).filter(isNonNegativeInt);

  const statusesFromParams = ensureArray(paramStatuses).filter((i) =>
    ['open', 'closed', 'new', 'no reply'].includes(i)
  );

  const startingFilterString = buildFilterQueries(
    tagsFromParams,
    channelsFromParams,
    statusesFromParams,
    [...agentsFromParams, ...teamsFromParams]
  );

  const {
    results,
    count,
    status,
    pagination,
    massAction,
    setSelectedChannels,
    setActionTimestamp,
    setSelectedTags,
    setSelectedStatuses,
    setSelectedAgentsAndTeams,
    selectedChannels,
    selectedTags,
    selectedStatuses,
    selectedAgentsAndTeams,
    applyFilters,
  } = useResultsData(searchTerm, startingFilterString);

  useEffect(() => {
    setSelectedChannels(channelsFromParams);
    setSelectedTags(tagsFromParams);
    setSelectedStatuses(statusesFromParams);
    setSelectedAgentsAndTeams([...agentsFromParams, ...teamsFromParams]);
  }, []);

  const openSearchResultModal = (messages, conversationId, event) => {
    event.stopPropagation();

    new GeckoModalService().open(() => {
      return (
        <MessageListModal
          messages={messages}
          conversationId={conversationId}
          searchTerm={searchTerm}
        />
      );
    });
  };

  const openCloseConversationModal = (conversation) => {
    new GeckoModalService().open(() => {
      return (
        <CloseConversationModal
          setActionTimestamp={setActionTimestamp}
          conversation={conversation}
        />
      );
    });
  };

  const openAddTagsModal = (conversation) => {
    new GeckoModalService().open(() => {
      return (
        <AddTagsModal
          setActionTimestamp={setActionTimestamp}
          conversation={conversation}
        />
      );
    });
  };

  const openAssignConversationModal = (conversation) => {
    new GeckoModalService().open(() => {
      return (
        <AssignConversationModal
          setActionTimestamp={setActionTimestamp}
          conversation={conversation}
        />
      );
    });
  };

  const openMassAddTagsModal = () => {
    new GeckoModalService().open(() => {
      return (
        <AddMassTagsModal
          setActionTimestamp={setActionTimestamp}
          massAction={massAction}
        />
      );
    });
  };

  const openMassCloseConversationModal = () => {
    new GeckoModalService().open(() => {
      return (
        <MassCloseConversationModal
          setActionTimestamp={setActionTimestamp}
          massAction={massAction}
        />
      );
    });
  };

  const openMassAssignmConversationModal = () => {
    new GeckoModalService().open(() => {
      return (
        <MassAssignConversationModal
          setActionTimestamp={setActionTimestamp}
          massAction={massAction}
        />
      );
    });
  };

  const cols = [
    {
      key: 'contact',
      name: 'Contact',
      sortable: true,
    },
    {
      key: 'messages',
      name: 'Conversation',
      width: '38%',
      render: (messages = [], conversation) => {
        const getDisplayMessage = (messages = []) => {
          return messages && messages.length ? (
            <>
              <div className="conversation-message">
                <HighlightedText
                  text={frontTruncate(messages[0].content || '', searchTerm)}
                  term={searchTerm}
                />
              </div>
              <div className="conversation-message-date">
                {getReceivedAt(messages[0].received_at)}
              </div>
            </>
          ) : null;
        };

        const getExtraMessagesText = (messages) => {
          return `+ ${messages.length - 1} more message${
            messages.length === 2 ? '' : 's'
          }`;
        };

        return (
          <div className="conversation-cell">
            {getDisplayMessage(messages)}
            {messages && messages.length > 1 ? (
              <GeckoBtn
                className="more-messages-button"
                size={GECKO_CONSTS.BTN_SIZE_SM}
                type={GECKO_CONSTS.BTN_TYPE_NAKED}
                name={getExtraMessagesText(messages)}
                handler={(event) =>
                  openSearchResultModal(messages, conversation.id, event)
                }
              >
                {getExtraMessagesText(messages)}
              </GeckoBtn>
            ) : null}
          </div>
        );
      },
    },
    {
      key: 'lastMessageTime',
      name: 'Last Message',
      sortable: true,
      render: (value) => {
        return <div>{fromNow(value)}</div>;
      },
    },
    {
      key: 'assignedTo',
      name: 'Assigned To',
    },
    {
      key: 'lastChannel',
      name: 'Last Channel',
      sortable: true,
      icon: 'far fa-question-circle',
      description:
        'Conversations can take place across multiple channels. This is the channel that last received a message from the contact.',
    },
    {
      key: 'status',
      name: 'Status',
      sortable: true,
      render: (value) => {
        const getType = (value) => {
          switch (value) {
            case 'open':
              return 'warning';
            case 'closed':
              return 'success';
            default:
              return 'default';
          }
        };
        return <GeckoLabel name={value} type={getType(value)} />;
      },
    },
    {
      type: GECKO_CONSTS.TABLE_CELL_TYPE_DROPDOWN,
      items: [
        {
          name: 'Close conversation',
          handler: (conversation) => openCloseConversationModal(conversation),
          hideWhen: (conversation) => conversation.status === 'closed',
        },
        {
          name: 'Add / Edit tags',
          handler: (conversation) => openAddTagsModal(conversation),
        },
        {
          name: 'Assign agents / teams',
          handler: (conversation) => openAssignConversationModal(conversation),
        },
      ],
    },
  ];

  const headerButtons = [
    {
      name: 'Actions',
      preset: GECKO_CONSTS.BTN_PRESET_ACTIONS,
      align: 'right',
      type: 'default',
      hideWhen: () => !massAction.selected.length,
      items: [
        {
          name: 'Add tags',
          handler: () => openMassAddTagsModal(),
        },
        {
          name: 'Close conversations',
          handler: () => openMassCloseConversationModal(),
        },
        {
          name: 'Assign agents / teams',
          handler: () => openMassAssignmConversationModal(),
        },
      ],
    },
  ];

  const handleConversationClick = () => {
    GeckoLocation.goTo('/conversations', {
      filter: CONSTS.FILTER_MY_CONVERSATIONS,
    });
  };

  const handleRowClick = ({ messages }) => {
    if (messages && messages.length > 0) {
      GeckoLocation.goTo(
        `/conversations/${messages[0].conversation_id}?filter=Contact&contact=${messages[0].sender_id}&message=${messages[0].id}`
      );
    }
  };

  const canUseMassAction = GeckoStore.redux.get('appState.isSupport');

  return (
    <>
      {!webAvailable && !mobileAvailable ? <NavBar /> : null}
      <Loader isLoading={status === 'loading'} spinnerPageCenter>
        {status === 'success' && (
          <>
            {searchTerm.length < 3 ? (
              <ConversationErrorContainer
                title="No Conversations to Display"
                icon="far fa-search-minus"
                description="Please search again using at least 3 characters."
                button={{
                  name: 'Back to conversations',
                  type: 'primary',
                  icon: 'far fa-comments',
                  handler: () => handleConversationClick(),
                }}
              />
            ) : (
              <>
                <ResultsHeader count={count} searchTerm={searchTerm} />
                <div className="results-table content-margin">
                  <GeckoCardTable
                    rows={results}
                    cols={cols}
                    rowHandler={handleRowClick}
                    pagination={pagination}
                    massAction={canUseMassAction ? massAction : null}
                    headerBtns={headerButtons}
                    filter={
                      <SearchFilterBar
                        {...{
                          selectedAgentsAndTeams,
                          selectedChannels,
                          selectedTags,
                          selectedStatuses,
                          setSelectedAgentsAndTeams,
                          setSelectedChannels,
                          setSelectedTags,
                          setSelectedStatuses,
                        }}
                        onApplyFilter={(value) => applyFilters(value)}
                      />
                    }
                  />
                </div>
              </>
            )}
          </>
        )}
      </Loader>
    </>
  );
}

export default Conversations;
